/* ============================================================
   PDP — thumbnail swap + variant picker (option change updates
   the hidden variant id, price, and submit button state).
   ============================================================ */
(function () {
  // ---- Navigable gallery (scroll-snap track + arrows + thumbs + lightbox) ----
  function initGallery() {
    const stage = document.querySelector('[data-pdp-stage]');
    if (!stage) return;
    const slides = Array.prototype.slice.call(stage.querySelectorAll('[data-pdp-slide]'));
    if (!slides.length) return;
    const thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-pdp-thumb]'));
    const prev = document.querySelector('[data-pdp-prev]');
    const next = document.querySelector('[data-pdp-next]');

    let active = 0;

    function setActive(i) {
      active = i;
      thumbs.forEach((t, idx) => t.classList.toggle('on', idx === i));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    }

    function goTo(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      // offsetLeft is relative to the shared offset parent, so the delta
      // from the first slide is the scrollLeft the track needs.
      stage.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    }

    thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));
    if (prev) prev.addEventListener('click', () => goTo(active - 1));
    if (next) next.addEventListener('click', () => goTo(active + 1));

    // Track the centered slide → active thumbnail + arrow states.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          const idx = slides.indexOf(e.target);
          if (idx !== -1) setActive(idx);
        }
      });
    }, { root: stage, threshold: [0.6] });
    slides.forEach((s) => io.observe(s));

    setActive(0);

    // Lightbox
    const lb = document.querySelector('[data-pdp-lightbox]');
    if (lb) {
      const lbImg = lb.querySelector('[data-pdp-lightbox-img]');
      const closeBtn = lb.querySelector('[data-pdp-lightbox-close]');
      const lbPrev = lb.querySelector('[data-pdp-lightbox-prev]');
      const lbNext = lb.querySelector('[data-pdp-lightbox-next]');
      const multi = slides.length > 1;
      let lbLastFocused = null;
      let lbIndex = 0;

      // Single-image products don't need the prev/next controls.
      if (lbPrev) lbPrev.hidden = !multi;
      if (lbNext) lbNext.hidden = !multi;

      function showImage(i) {
        lbIndex = Math.max(0, Math.min(slides.length - 1, i));
        const img = slides[lbIndex].querySelector('img');
        if (img) { lbImg.src = img.currentSrc || img.src; lbImg.alt = img.alt || ''; }
        if (lbPrev) lbPrev.disabled = lbIndex === 0;
        if (lbNext) lbNext.disabled = lbIndex === slides.length - 1;
        // Don't strand focus on a button that just became disabled.
        if (document.activeElement && document.activeElement.disabled && closeBtn) closeBtn.focus();
      }

      const onKeydown = (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); showImage(lbIndex + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); showImage(lbIndex - 1); }
        else if (e.key === 'Tab') {
          const f = Array.prototype.slice.call(lb.querySelectorAll('button:not([disabled]):not([hidden])'));
          if (!f.length) return;
          const first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };

      const open = (i, trigger) => {
        lbLastFocused = trigger || document.activeElement;
        showImage(i);
        lb.classList.add('open');
        lb.setAttribute('aria-hidden', 'false');
        lb.addEventListener('keydown', onKeydown);
        setTimeout(() => { if (closeBtn) closeBtn.focus(); }, 0);
      };
      const close = () => {
        lb.classList.remove('open');
        lb.setAttribute('aria-hidden', 'true');
        lbImg.removeAttribute('src');
        lb.removeEventListener('keydown', onKeydown);
        if (lbLastFocused && typeof lbLastFocused.focus === 'function') lbLastFocused.focus();
        lbLastFocused = null;
      };
      slides.forEach((s, i) => s.addEventListener('click', () => {
        if (!s.querySelector('img')) return;
        s.tabIndex = -1;
        open(i, s);
      }));
      if (lbPrev) lbPrev.addEventListener('click', () => showImage(lbIndex - 1));
      if (lbNext) lbNext.addEventListener('click', () => showImage(lbIndex + 1));
      if (closeBtn) closeBtn.addEventListener('click', close);
      lb.addEventListener('click', (e) => { if (e.target === lb) close(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && lb.classList.contains('open')) close();
      });
    }
  }

  // ---- Variant picker ----
  function initVariantPicker() {
    const form = document.querySelector('[data-pdp-form]');
    if (!form) return;

    const variantInput = form.querySelector('[data-pdp-variant-id]');
    const priceEl = document.querySelector('[data-pdp-price]');
    const submitBtn = form.querySelector('[data-pdp-submit]');
    const variantsEl = form.querySelector('[data-pdp-variants]');
    if (!variantsEl || !variantInput) return;

    const notifyBox = document.querySelector('[data-pdp-notify]');
    const notifyTags = document.querySelector('[data-pdp-notify-tags]');

    let variants;
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    const invEl = document.getElementById('pdp-inventory');
    let inv = {};
    try { if (invEl) inv = JSON.parse(invEl.textContent); } catch (e) {}

    function currentSelection() {
      const selected = [];
      form.querySelectorAll('[data-pdp-option]:checked').forEach(r => selected.push(r.value));
      return selected;
    }

    function findVariant(selection) {
      if (!selection.length) return variants[0];
      return variants.find(v => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        if (opts.length !== selection.length) return false;
        return opts.every((o, i) => o === selection[i]);
      });
    }

    function formatMoney(cents) {
      // Match the Liquid `money_with_currency` format ("$70.00 CAD", "£56.00 GBP"), not Intl's "CA$70.00".
      // narrowSymbol keeps dollar currencies as a bare "$"; the trailing code disambiguates.
      var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
      try {
        return (cents / 100).toLocaleString('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }) + ' ' + code;
      } catch (e) {
        return '$' + (cents / 100).toFixed(2) + ' ' + code;
      }
    }

    function updateQty(v) {
      const qtyRow = document.getElementById('pdp-qty-row');
      if (!qtyRow) return;
      const qtyInput = qtyRow.querySelector('[data-pdp-qty]');
      const tracked = v.inventory_management === 'shopify';
      const stock = inv[v.id] || 0;
      // Show when: available AND (untracked OR stock > 1)
      const show = v.available && (!tracked || stock > 1);
      qtyRow.style.display = show ? '' : 'none';
      if (qtyInput) {
        if (tracked && stock > 0) {
          qtyInput.max = stock;
          qtyInput.value = Math.min(parseInt(qtyInput.value) || 1, stock);
        } else {
          qtyInput.removeAttribute('max');
        }
      }
    }

    // Scarcity line: "One of a kind" for a single piece, "Only N left" for a
    // tracked batch. Hidden when untracked (permanently in stock) or sold out.
    function updateStockCounter(v) {
      const el = document.querySelector('[data-pdp-stock-counter]');
      if (!el) return;
      const tracked = v.inventory_management === 'shopify';
      const stock = inv[v.id] || 0;
      if (!tracked || stock <= 0) { el.hidden = true; return; }
      if (stock === 1) {
        el.textContent = el.dataset.singleLabel || 'One of a kind';
        el.classList.add('pdp-stock--single');
      } else {
        const tmpl = el.dataset.multiLabel || 'Only {n} left';
        el.textContent = tmpl.replace('{n}', stock);
        el.classList.remove('pdp-stock--single');
      }
      el.hidden = false;
    }

    function handleize(s) {
      return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    function updateNotify(v) {
      if (!notifyBox) return;
      notifyBox.hidden = v.available;
      if (notifyTags) {
        let tags = notifyTags.dataset.tagsBase;
        if (v.title && v.title !== 'Default Title') {
          tags += ',restock:' + notifyTags.dataset.productHandle + '--' + handleize(v.title);
        }
        notifyTags.value = tags;
      }
    }

    function update() {
      const sel = currentSelection();
      const v = findVariant(sel);

      // Highlight selected chip
      form.querySelectorAll('.pdp-swatch').forEach(label => {
        const input = label.querySelector('[data-pdp-option]');
        label.classList.toggle('on', !!input && input.checked);
      });

      if (!v) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Unavailable'; }
        return;
      }

      variantInput.value = v.id;

      if (priceEl) {
        let html = formatMoney(v.price);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          html += ` <span class="pdp-compare">${formatMoney(v.compare_at_price)}</span>`;
        }
        priceEl.innerHTML = html;
      }

      if (submitBtn) {
        submitBtn.disabled = !v.available;
        submitBtn.textContent = v.available ? 'Add to cart' : 'Sold out';
      }

      updateQty(v);
      updateStockCounter(v);
      updateNotify(v);

      // Sync URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url.toString());
    }

    form.addEventListener('change', (e) => {
      if (e.target && e.target.matches('[data-pdp-option]')) update();
    });

    // +/- quantity buttons
    form.addEventListener('click', (e) => {
      const dec = e.target.closest('[data-pdp-qty-dec]');
      const inc = e.target.closest('[data-pdp-qty-inc]');
      if (!dec && !inc) return;
      const input = form.querySelector('[data-pdp-qty]');
      if (!input) return;
      const current = parseInt(input.value) || 1;
      const max = parseInt(input.max) || 999;
      input.value = dec ? Math.max(1, current - 1) : Math.min(max, current + 1);
    });

    // Set initial state on page load
    update();

    // Restock notify: success + server-error markup is Liquid-rendered inside the
    // customer form (bottom of column, works without JS); relocate it into the box.
    if (notifyBox) {
      const ok = document.querySelector('[data-pdp-notify-posted]');
      if (ok) {
        notifyBox.hidden = false;
        const fields = notifyBox.querySelector('[data-pdp-notify-fields]');
        if (fields) fields.hidden = true;
        notifyBox.appendChild(ok);
      }
      const err = document.querySelector('[data-pdp-notify-error]');
      if (err) { notifyBox.hidden = false; notifyBox.appendChild(err); }
    }
  }

  // ---- Mobile nav + scroll hide ----
  function initNav() {
    var menu = document.getElementById('menuOverlay');
    var hamburger = document.getElementById('hamburgerBtn');
    if (!hamburger || !menu) return;

    var lastFocused = null;

    function getFocusableElements(container) {
      return Array.prototype.slice.call(container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) {
        // Skip elements that are not currently focusable (display: none / visibility hidden).
        return el && el.offsetParent !== null;
      });
    }

    function focusFirst(container) {
      var focusables = getFocusableElements(container);
      if (focusables.length) focusables[0].focus();
    }

    function trapTabKey(e) {
      if (e.key !== 'Tab') return;
      var focusables = getFocusableElements(menu);
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function openMenu() {
      lastFocused = document.activeElement;

      menu.classList.add('active');
      hamburger.classList.add('active');
      hamburger.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');

      menu.addEventListener('keydown', trapTabKey);
      setTimeout(function () { focusFirst(menu); }, 0);
    }

    function closeMenu() {
      menu.classList.remove('active');
      hamburger.classList.remove('active');
      hamburger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');

      menu.removeEventListener('keydown', trapTabKey);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      } else if (hamburger) {
        hamburger.focus();
      }
      lastFocused = null;
    }

    hamburger.addEventListener('click', function () {
      var isOpen = menu.classList.contains('active');
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    menu.querySelectorAll('.menu-link').forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!menu.classList.contains('active')) return;
      closeMenu();
    });

    var nav = document.querySelector('.site-nav');
    if (nav) {
      var lastY = 0;
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        if (y > lastY && y > 80) nav.classList.add('site-nav--hidden');
        else nav.classList.remove('site-nav--hidden');
        lastY = y;
      }, { passive: true });
    }
  }

  // ---- Shop grid: sort chips + sold-out ordering ----
  function initShopGrid() {
    document.querySelectorAll('[data-sort]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var url = new URL(window.location.href);
        var sort = el.getAttribute('data-sort');
        if (sort) url.searchParams.set('sort_by', sort);
        else url.searchParams.delete('sort_by');
        window.location.href = url.toString();
      });
    });

    var grid = document.querySelector('.shop-grid');
    if (grid) {
      grid.querySelectorAll('[data-sold-out="true"]').forEach(function (card) {
        grid.appendChild(card);
      });
    }
  }

  // ---- Info/FAQ sidebar nav (scroll-spy + smooth scroll) ----
  function initInfo() {
    var root = document.querySelector('[data-info]');
    if (!root) return;

    function setActive(id) {
      root.querySelectorAll('.info-nav-link').forEach(function (link) {
        link.classList.toggle('on', link.getAttribute('data-nav-target') === id);
      });
    }

    function scrollToSection(id) {
      var target = document.getElementById('info-' + id);
      if (!target) return;
      // Land the heading level with the sticky sidebar label (top: 100px).
      // Scrolling the article top there instead leaves the section's 48px top
      // padding above it — enough to expose two lines of the previous section
      // whenever the hide-on-scroll nav is off-screen (downward jumps).
      var heading = target.querySelector('.info-section-heading') || target;
      var offset = heading.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: offset, behavior: 'smooth' });
      setActive(id);
    }

    root.addEventListener('click', function (e) {
      var link = e.target.closest('.info-nav-link');
      if (!link) return;
      e.preventDefault();
      var id = link.getAttribute('data-nav-target');
      scrollToSection(id);
      if (history.replaceState) history.replaceState(null, '', '#info-' + id);
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            setActive(entry.target.id.replace('info-', ''));
          }
        });
      }, { rootMargin: '-10% 0px -70% 0px', threshold: 0 });

      root.querySelectorAll('.info-section').forEach(function (s) {
        observer.observe(s);
      });
    }

    if (window.location.hash.indexOf('#info-') === 0) {
      var hashId = window.location.hash.replace('#info-', '');
      setTimeout(function () { scrollToSection(hashId); }, 100);
    }
  }

  function init() { initGallery(); initVariantPicker(); initNav(); initShopGrid(); initInfo(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
